import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/clinical/PageHeader";
import { ReviewNote } from "@/components/clinical/ReviewNote";
import { EmptyState } from "@/components/clinical/EmptyState";
import { AdvancedCurveExplorer } from "@/components/clinical/AdvancedCurveExplorer";
import { DateOnlyImportEventsPanel } from "@/components/clinical/DateOnlyImportEventsPanel";
import { PsyLogTimelinePanel } from "@/components/clinical/PsyLogTimelinePanel";
import { buildClinicalIntelligenceSummary } from "@/domain/clinicalIntelligenceEngine";
import { normalizeVitalRecord, vitalSummary } from "@/lib/clinical/vitalAssessment";
import { Activity, BrainCircuit, CalendarDays, HeartPulse, LineChart as LineChartIcon, Target, TriangleAlert, Upload } from "lucide-react";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";

async function safeQuery(label: string, builder: () => any) {
  try {
    const { data, error } = await builder();
    if (error) {
      console.warn(`[Curves] ${label} unavailable`, error);
      return [];
    }
    return data ?? [];
  } catch (error) {
    console.warn(`[Curves] ${label} failed`, error);
    return [];
  }
}

export default function Curves() {
  const { data: patients = [] } = usePatients();
  const [patientId, setPatientId] = useState(() => localStorage.getItem(PATIENT_STORAGE_KEY) ?? "");
  const [days, setDays] = useState(30);
  const sinceIso = useMemo(() => new Date(Date.now() - days * 24 * 3_600_000).toISOString(), [days]);
  const sinceDate = useMemo(() => sinceIso.slice(0, 10), [sinceIso]);
  const patient = patients.find((row: any) => row.id === patientId);

  useEffect(() => {
    if (!patientId || !patients.length) return;
    if (!patients.some((row: any) => row.id === patientId)) setPatientId("");
  }, [patientId, patients]);

  function choosePatient(value: string) {
    setPatientId(value);
    if (value) localStorage.setItem(PATIENT_STORAGE_KEY, value);
  }

  const { data: medications = [] } = useQuery({
    queryKey: ["psylog-medications", patientId],
    enabled: !!patientId,
    queryFn: () => safeQuery("patient_medications", () => (supabase as any)
      .from("patient_medications")
      .select("*, substances(*), substance_formulations(*)")
      .eq("patient_id", patientId)
      .order("status")
      .order("usual_time")),
  });

  const { data: doses = [] } = useQuery({
    queryKey: ["curves-doses", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("medication_dose_logs", () => (supabase as any)
      .from("medication_dose_logs")
      .select("*, substances(*), substance_formulations(*)")
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .gte("actual_time", sinceIso)
      .order("actual_time", { ascending: true })),
  });

  const { data: targetSymptoms = [] } = useQuery({
    queryKey: ["psylog-targets", patientId],
    enabled: !!patientId,
    queryFn: () => safeQuery("patient_target_symptoms", () => (supabase as any)
      .from("patient_target_symptoms")
      .select("*")
      .eq("patient_id", patientId)
      .eq("status", "ativo")
      .is("deleted_at", null)
      .order("priority")),
  });

  const { data: symptomMeasurements = [] } = useQuery({
    queryKey: ["psylog-symptoms", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("symptom_measurements", () => (supabase as any)
      .from("symptom_measurements")
      .select("*, patient_target_symptoms(symptom_name)")
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .gte("measured_at", sinceIso)
      .order("measured_at")),
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ["psylog-checkins", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("session_checkins", () => (supabase as any)
      .from("session_checkins")
      .select("*")
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .gte("checkin_at", sinceIso)
      .order("checkin_at")),
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["psylog-sessions", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("clinical_sessions", () => (supabase as any)
      .from("clinical_sessions")
      .select("*")
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .gte("session_at", sinceIso)
      .order("session_at")),
  });

  const { data: substanceUse = [] } = useQuery({
    queryKey: ["psylog-substance-use", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("substance_use_logs", () => (supabase as any)
      .from("substance_use_logs")
      .select("*")
      .eq("patient_id", patientId)
      .gte("used_at", sinceIso)
      .order("used_at")),
  });

  const { data: psylogEvents = [] } = useQuery({
    queryKey: ["psylog-events", patientId, days],
    enabled: !!patientId,
    queryFn: () => safeQuery("patient_psylog_events", () => (supabase as any)
      .from("patient_psylog_events")
      .select("*")
      .eq("patient_id", patientId)
      .is("deleted_at", null)
      .gte("event_at", sinceIso)
      .order("event_at")),
  });

  const { data: dateOnlyImports = [] } = useQuery({
    queryKey: ["clinical-import-events", patientId, days, "date-only"],
    enabled: !!patientId,
    queryFn: () => safeQuery("clinical_import_events date_only", () => (supabase as any)
      .from("clinical_import_events")
      .select("*")
      .eq("patient_id", patientId)
      .eq("time_precision", "date_only")
      .is("deleted_at", null)
      .gte("local_date", sinceDate)
      .order("local_date", { ascending: false })),
  });

  const { data: canonicalVitals = [] } = useQuery({
    queryKey: ["psylog-vitals", patientId, days, "canonical"],
    enabled: !!patientId,
    queryFn: () => safeQuery("vital_signs", () => (supabase as any)
      .from("vital_signs")
      .select("*")
      .eq("patient_id", patientId)
      .gte("measured_at", sinceIso)
      .order("measured_at")),
  });

  const { data: legacyVitals = [] } = useQuery({
    queryKey: ["psylog-vitals", patientId, days, "legacy"],
    enabled: !!patientId,
    queryFn: () => safeQuery("patient_vital_signs", () => (supabase as any)
      .from("patient_vital_signs")
      .select("*")
      .eq("patient_id", patientId)
      .gte("measured_at", sinceIso)
      .order("measured_at")),
  });

  const vitalSigns = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...(canonicalVitals as any[]).filter((row) => !row.deleted_at).map((row) => normalizeVitalRecord({ ...row, __source_table: "vital_signs" })),
      ...(legacyVitals as any[]).filter((row) => !row.deleted_at).map((row) => normalizeVitalRecord({ ...row, __source_table: "patient_vital_signs" })),
    ].filter((row) => {
      const key = row.id ? `${row.__source_table}:${row.id}` : `${row.measured_at}:${row.heart_rate_bpm ?? ""}:${row.systolic_mm_hg ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [canonicalVitals, legacyVitals]);
  const vitalStats = useMemo(() => vitalSummary(vitalSigns), [vitalSigns]);

  const clinicalSummary = useMemo(() => patientId ? buildClinicalIntelligenceSummary({
    patient,
    medications,
    doses,
    checkins,
    sessions,
    substanceUse,
    targetSymptoms,
    symptomMeasurements,
    periodDays: days,
  }) : null, [patientId, patient, medications, doses, checkins, sessions, substanceUse, targetSymptoms, symptomMeasurements, days]);

  const totalEvents = doses.length + symptomMeasurements.length + checkins.length + sessions.length + substanceUse.length + psylogEvents.length + vitalSigns.length + dateOnlyImports.length;

  return (
    <div className="min-w-0 space-y-5">
      <PageHeader
        title="Curvas / Timeline"
        description="Curva PK/PD relativa e linha temporal clínica por data real: doses, sintomas, check-ins, FC/PA/SpO₂, sessões e arquivos importados."
        actions={<Link to="/importar"><Button variant="outline"><Upload className="h-4 w-4 mr-1.5" />Importar dados</Button></Link>}
      />
      <ReviewNote>100 representa intensidade relativa estimada da própria substância, não concentração sérica. Eventos sem horário permanecem fora da curva; associação temporal orienta hipóteses, mas não prova causalidade.</ReviewNote>

      <Card className="p-4">
        <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(0,1fr)_170px_auto] md:items-end">
          <div className="min-w-0 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Paciente</Label>
            <select className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={patientId} onChange={(event) => choosePatient(event.target.value)}>
              <option value="">Selecione…</option>
              {patients.map((row: any) => <option key={row.id} value={row.id}>{row.social_name || row.full_name}</option>)}
            </select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Período</Label>
            <select className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm" value={days} onChange={(event) => setDays(Number(event.target.value))}>
              <option value={1}>24 horas</option><option value={3}>3 dias</option><option value={7}>7 dias</option><option value={14}>14 dias</option><option value={30}>30 dias</option><option value={60}>60 dias</option><option value={90}>90 dias</option>
            </select>
          </div>
          {patientId ? <Badge variant="outline" className="h-10 justify-center px-3">{totalEvents} eventos datados</Badge> : <div />}
        </div>
      </Card>

      {!patientId ? (
        <EmptyState icon={<LineChartIcon className="h-5 w-5" />} title="Selecione um paciente" description="A Timeline cruza doses, sintomas, sessões, PA e contexto somente após selecionar o perfil." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-6">
            <MetricCard icon={<Activity className="h-3.5 w-3.5" />} label="Doses" value={doses.length} />
            <MetricCard icon={<Target className="h-3.5 w-3.5" />} label="Sintomas" value={symptomMeasurements.length} />
            <MetricCard icon={<TriangleAlert className="h-3.5 w-3.5" />} label="Redose/sobreposição" value={clinicalSummary?.redoseCount ?? 0} />
            <MetricCard icon={<HeartPulse className="h-3.5 w-3.5" />} label="FC / vitais" value={vitalSigns.length ? `${vitalStats.latest?.heart_rate_bpm ?? "—"} bpm` : "—"} />
            <MetricCard icon={<CalendarDays className="h-3.5 w-3.5" />} label="Sem horário" value={dateOnlyImports.length} />
            <MetricCard icon={<BrainCircuit className="h-3.5 w-3.5" />} label="Qualidade" value={`${clinicalSummary?.dataQualityScore ?? 0}/100`} />
          </div>

          <AdvancedCurveExplorer
            doses={doses as any}
            title="Curvas relativas por substância e carga total"
            description="Marque/desmarque substâncias, use 'Só esta', alterne entre gráfico integrado e gráficos separados e acompanhe a linha AGORA com fase, cauda/carry-over e leitura contextual."
            bodyWeightKg={patient?.weight_kg == null ? null : Number(patient.weight_kg)}
          />

          <PsyLogTimelinePanel
            doses={doses}
            symptomMeasurements={symptomMeasurements}
            checkins={checkins}
            sessions={sessions}
            substanceUse={substanceUse}
            psylogEvents={psylogEvents}
            vitalSigns={vitalSigns}
          />

          <DateOnlyImportEventsPanel events={dateOnlyImports} />

          <Card className="space-y-3 p-4">
            <div className="flex items-center gap-2 font-medium"><BrainCircuit className="h-4 w-4" />Síntese temporal para revisão médica</div>
            {!clinicalSummary?.actionCards.length ? (
              <div className="text-sm text-muted-foreground">Ainda não há sinais suficientes. Registre formulação, dose real, sono/cafeína, sintoma-alvo e check-in no pico/offset.</div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {clinicalSummary.actionCards.slice(0, 8).map((card) => (
                  <div key={card.id} className="space-y-1 rounded-lg border p-3">
                    <div className="flex justify-between gap-2"><div className="text-sm font-medium">{card.title}</div><Badge variant="outline" className="text-[9px]">{card.severity}</Badge></div>
                    <div className="text-xs text-muted-foreground">{card.rationale}</div>
                    <div className="text-xs"><b>Manejo a revisar:</b> {card.action}</div>
                    <div className="text-[10px] text-muted-foreground">Confiança {card.confidence}; não prova causalidade.</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return <Card className="min-w-0 p-3"><div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">{icon}{label}</div><div className="mt-1 truncate text-xl font-semibold">{value}</div></Card>;
}
